/**
 * Badge Form Modal Component
 * Modal for creating and editing badge definitions with icon upload
 * Supports real-time validation and badge ID uniqueness checking
 */

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { type BadgeDefinition } from '../../lib/types/badge';
import {
  useCreateBadge,
  useUpdateBadge,
  useUploadBadgeIcon,
} from '../../hooks/useBadgeApi';
import { useAdminEntitlementsList } from '../../hooks/useAdminEntitlementsList';
import { authenticatedApi } from '../../lib/authenticated-api';
import { FormButton } from '../form/FormButton';
import { AnimatedMessage } from '../ui/AnimatedMessage';
import { BadgeIconUploader } from './BadgeIconUploader';
import { cn } from '../../lib/utils';

interface BadgeFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  badge?: BadgeDefinition | null; // null for create, BadgeDefinition for edit
}

// Badge validation schema
const badgeFormSchema = z.object({
  badgeId: z
    .string()
    .min(3, 'Badge ID must be at least 3 characters')
    .max(30, 'Badge ID must be at most 30 characters')
    .regex(
      /^[a-z0-9_]+$/,
      'Only lowercase letters, numbers, and underscores allowed'
    ),
  name: z
    .string()
    .min(1, 'Name is required')
    .max(50, 'Name must be at most 50 characters'),
  description: z
    .string()
    .min(1, 'Description is required')
    .max(200, 'Description must be at most 200 characters'),
});

type BadgeFormData = z.infer<typeof badgeFormSchema> & {
  icon?: File | null;
};

export function BadgeFormModal({
  isOpen,
  onClose,
  badge,
}: BadgeFormModalProps) {
  const isEditMode = !!badge;
  const [checkingBadgeId, setCheckingBadgeId] = useState(false);
  const [badgeIdError, setBadgeIdError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // Create-mode checkbox. In edit mode the badge's own flag governs, because
  // the API will not let it change (#1975).
  const [imageless, setImageless] = useState(false);
  // The linked entitlement (#2061); '' means unlinked. Kept outside the zod
  // schema because it is optional and drawn from the catalogue, not typed.
  const [linkedEntitlement, setLinkedEntitlement] = useState('');
  const originalLink = badge?.linked_entitlement ?? '';
  // Automatic-only (#2268). Meaningful only with a link: the box is disabled
  // without one, and `automaticOnlyEffective` is what is sent and compared.
  const [automaticOnly, setAutomaticOnly] = useState(false);
  const originalAutomaticOnly = badge?.automatic_only === true;
  const automaticOnlyEffective = automaticOnly && !!linkedEntitlement;

  const {
    data: entitlementCatalogue,
    isLoading: isLoadingCatalogue,
    isError: isCatalogueError,
  } = useAdminEntitlementsList();
  const catalogueEntitlements = entitlementCatalogue?.entitlements ?? [];
  const linkedDefinition = catalogueEntitlements.find(
    e => e.id === linkedEntitlement
  );
  // The naming convention for a badge linked to `<ns>:<name>`: id
  // `<ns>_<name>`, the catalogue name as display name. A hint, never enforced.
  const suggestedBadgeId = linkedEntitlement.replace(/:/g, '_');
  const suggestedName = linkedDefinition?.name;
  // A stored link the catalogue no longer lists (a retired id) is still shown,
  // so opening the editor never silently presents the badge as unlinked.
  const linkMissingFromCatalogue =
    !!originalLink &&
    !!entitlementCatalogue &&
    !catalogueEntitlements.some(e => e.id === originalLink);

  // The single question the rest of the component asks: does this badge have
  // an icon by design or not? Deriving it once stops create mode and edit mode
  // drifting apart, which is how the uploader ended up being offered for a
  // badge the API refuses to give an icon.
  const isImageless = isEditMode ? badge?.imageless === true : imageless;

  const createBadge = useCreateBadge();
  const updateBadge = useUpdateBadge();
  const uploadBadgeIcon = useUploadBadgeIcon();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid },
    setError,
    clearErrors,
  } = useForm<BadgeFormData>({
    resolver: zodResolver(badgeFormSchema),
    defaultValues: {
      badgeId: badge?.badgeId ?? '',
      name: badge?.name ?? '',
      description: badge?.description ?? '',
    },
    mode: 'onChange',
  });

  // Watch for badge ID changes to check uniqueness
  const watchedBadgeId = watch('badgeId');

  // Reset form when modal opens/closes or badge changes
  useEffect(() => {
    if (isOpen) {
      reset({
        badgeId: badge?.badgeId ?? '',
        name: badge?.name ?? '',
        description: badge?.description ?? '',
      });
      setBadgeIdError(null);
      setSelectedFile(null);
      setUploadError(null);
      setImageless(false);
      setLinkedEntitlement(badge?.linked_entitlement ?? '');
      setAutomaticOnly(badge?.automatic_only === true);
    }
  }, [isOpen, badge, reset]);

  // Check badge ID uniqueness (only in create mode)
  useEffect(() => {
    if (!isEditMode && watchedBadgeId && watchedBadgeId.length >= 3) {
      const checkBadgeId = async () => {
        setCheckingBadgeId(true);
        setBadgeIdError(null);
        clearErrors('badgeId');

        try {
          // Check if badge ID already exists using centralized API client (has timeout protection)
          const response =
            await authenticatedApi.checkBadgeExists(watchedBadgeId);

          if (response.success) {
            setBadgeIdError('This badge ID is already taken');
            setError('badgeId', {
              type: 'manual',
              message: 'This badge ID is already taken',
            });
          } else {
            // Badge doesn't exist (404), so it's available
            setBadgeIdError(null);
            clearErrors('badgeId');
          }
        } catch (_error) {
          // Any error means badge is available (most likely 404)
          setBadgeIdError(null);
          clearErrors('badgeId');
        } finally {
          setCheckingBadgeId(false);
        }
      };

      const timeoutId = setTimeout(checkBadgeId, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [watchedBadgeId, isEditMode, setError, clearErrors]);

  const onSubmit = async (data: BadgeFormData) => {
    setUploadError(null);

    try {
      if (isEditMode) {
        // Deliberately NOT requiring an icon here, even when the badge has
        // none. `icon_url: ''` is a state the API documents and returns on
        // purpose, so refusing to save a description fix until someone uploads
        // an image would contradict the contract and block unrelated repairs.
        // The uploader says the icon is missing; it does not hold the form
        // hostage.

        // Update existing badge
        // If file is selected, upload it first.
        //
        // An imageless badge never gets here with a file, because the uploader
        // is not rendered for one. The `!isImageless` guard is belt and braces
        // against a future edit re-introducing it: uploading would earn a 409
        // and strand the admin on a form that cannot succeed.
        if (selectedFile && !isImageless) {
          setIsUploading(true);
          try {
            await uploadBadgeIcon.mutateAsync({
              badgeId: badge.badgeId,
              file: selectedFile,
            });
          } catch (error) {
            setUploadError(
              error instanceof Error
                ? error.message
                : 'Failed to upload badge icon'
            );
            setIsUploading(false);
            return;
          }
          setIsUploading(false);
        }

        // Update badge metadata. The link is sent only when it changed:
        // `null` clears it, and omitting it leaves it alone. Automatic-only
        // (#2268) is sent when it changed, and also whenever the link did:
        // unlinking an automatic-only badge must clear the flag in the same
        // request, and linking must say what the admin chose, because a
        // flag stored on an unlinked badge is not shown here (the API omits
        // it) and would otherwise take effect with the new link.
        const linkChanged = linkedEntitlement !== originalLink;
        const automaticOnlyChanged =
          automaticOnlyEffective !== originalAutomaticOnly;
        await updateBadge.mutateAsync({
          badgeId: badge.badgeId,
          updates: {
            name: data.name,
            description: data.description,
            ...(linkChanged && {
              linked_entitlement: linkedEntitlement || null,
            }),
            ...((automaticOnlyChanged || linkChanged) && {
              automatic_only: automaticOnlyEffective,
            }),
          },
        });
      } else {
        // Create mode: an icon is required unless the badge is declared
        // imageless, which is the whole point of the checkbox.
        if (!imageless && !selectedFile) {
          setUploadError('Badge icon is required');
          return;
        }

        // Step 1: create the badge. It has no icon yet, and that is now how it
        // is represented — the server writes `icon_url: ''`.
        //
        // This used to send a dicebear placeholder URL, which was persisted and
        // then overwritten a second later by step 2. When step 2 failed it was
        // not overwritten, and the badge kept pointing at a third-party host
        // that every client then fetched on every render. `icon_url` and
        // `icon_updated_at` are no longer accepted by the API at all: they are
        // written only by the icon upload endpoint, which sanitises what it
        // stores. Sending either is a 400.
        await createBadge.mutateAsync({
          badgeId: data.badgeId,
          name: data.name,
          description: data.description,
          // Sent only when true. The API accepts `false`, but it means exactly
          // what omitting it means, and sending it would put a value in the
          // document that the response never echoes back.
          ...(imageless && { imageless: true }),
          ...(linkedEntitlement && { linked_entitlement: linkedEntitlement }),
          // Sent only when true, and only with the link it needs.
          ...(automaticOnlyEffective && { automatic_only: true }),
        });

        // Step 2: Upload the actual icon — skipped entirely when there is
        // deliberately none, because the badge is already complete and the
        // endpoint would answer 409.
        //
        // Written as a skip rather than an early return with its own copy of
        // the success tail below: a duplicated tail is a tail that stops being
        // shared the moment someone adds a line to one of them.
        if (!imageless) {
          // `selectedFile` cannot be null here: the guard at the top of this
          // branch returns unless there is one, and this block does not run in
          // the imageless case. TypeScript cannot follow that across two
          // separate conditions. Re-checking rather than asserting non-null,
          // because an assertion would silently paper over a regression if
          // either guard were ever changed.
          if (!selectedFile) {
            setUploadError('Badge icon is required');
            return;
          }

          setIsUploading(true);
          try {
            await uploadBadgeIcon.mutateAsync({
              badgeId: data.badgeId,
              file: selectedFile,
            });
          } catch (error) {
            setUploadError(
              error instanceof Error
                ? error.message
                : 'Badge created but icon upload failed. You can edit the badge to upload the icon.'
            );
            setIsUploading(false);
            return;
          }
          setIsUploading(false);
        }
      }

      reset();
      setSelectedFile(null);
      setUploadError(null);
      setImageless(false);
      setLinkedEntitlement('');
      setAutomaticOnly(false);
      onClose();
    } catch (_error) {
      // Error is handled by the mutation
    }
  };

  const handleClose = () => {
    if (createBadge.isPending || updateBadge.isPending || isUploading) return;
    reset();
    setBadgeIdError(null);
    setSelectedFile(null);
    setUploadError(null);
    onClose();
  };

  if (!isOpen) return null;

  const isLoading =
    createBadge.isPending || updateBadge.isPending || isUploading;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={isLoading ? undefined : handleClose}
        />

        {/* Modal */}
        <div className="inline-block w-full max-w-lg p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-lg">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              {isEditMode ? 'Edit Badge' : 'Create New Badge'}
            </h3>
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 disabled:opacity-50"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Badge ID (only for create mode) */}
            {!isEditMode && (
              <div>
                <label
                  htmlFor="badgeId"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Badge ID *
                </label>
                <input
                  {...register('badgeId')}
                  id="badgeId"
                  type="text"
                  placeholder="e.g., early_adopter"
                  disabled={isLoading}
                  className={cn(
                    'block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:border-sky-500 focus:ring-sky-500 sm:text-sm',
                    (errors.badgeId || badgeIdError) &&
                      'border-red-300 focus:border-red-500 focus:ring-red-500'
                  )}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Lowercase letters, numbers, and underscores only (3-30
                  characters)
                </p>
                {errors.badgeId && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {errors.badgeId.message}
                  </p>
                )}
                {badgeIdError && !errors.badgeId && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {badgeIdError}
                  </p>
                )}
                {checkingBadgeId && (
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Checking availability...
                  </p>
                )}
              </div>
            )}

            {/* Badge Name */}
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Name *
              </label>
              <input
                {...register('name')}
                id="name"
                type="text"
                placeholder="e.g., Early Adopter"
                disabled={isLoading}
                className={cn(
                  'block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:border-sky-500 focus:ring-sky-500 sm:text-sm',
                  errors.name &&
                    'border-red-300 focus:border-red-500 focus:ring-red-500'
                )}
              />
              <div className="flex justify-between mt-1">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Display name for the badge
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {watch('name')?.length || 0}/50
                </p>
              </div>
              {errors.name && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.name.message}
                </p>
              )}
            </div>

            {/* Badge Description */}
            <div>
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Description *
              </label>
              <textarea
                {...register('description')}
                id="description"
                rows={3}
                placeholder="e.g., Awarded to users who joined during the beta period"
                disabled={isLoading}
                className={cn(
                  'block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:border-sky-500 focus:ring-sky-500 sm:text-sm',
                  errors.description &&
                    'border-red-300 focus:border-red-500 focus:ring-red-500'
                )}
              />
              <div className="flex justify-between mt-1">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Brief description of the badge
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {watch('description')?.length || 0}/200
                </p>
              </div>
              {errors.description && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.description.message}
                </p>
              )}
            </div>

            {/* Linked entitlement (#2061) */}
            <div>
              <label
                htmlFor="linkedEntitlement"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Granted automatically by
              </label>
              <select
                id="linkedEntitlement"
                data-testid="badge-linked-entitlement"
                value={linkedEntitlement}
                onChange={e => setLinkedEntitlement(e.target.value)}
                disabled={isLoading || isLoadingCatalogue || isCatalogueError}
                className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
              >
                <option value="">
                  {isLoadingCatalogue
                    ? 'Loading entitlements…'
                    : 'No entitlement (granted by hand only)'}
                </option>
                {linkMissingFromCatalogue && (
                  <option value={originalLink}>
                    {originalLink} (not in the catalogue)
                  </option>
                )}
                {catalogueEntitlements.map(entitlement => (
                  <option key={entitlement.id} value={entitlement.id}>
                    {entitlement.name} ({entitlement.id})
                  </option>
                ))}
              </select>
              {isCatalogueError ? (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  The entitlement catalogue could not be loaded, so the link
                  cannot be changed here. Saving leaves it as it is.
                </p>
              ) : (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Everyone who holds this entitlement gets the badge, and loses
                  it when the entitlement goes. Badges granted by hand are never
                  removed, unless the badge is automatic-only. A new or changed
                  link reaches existing holders at the next hourly reconcile,
                  not straight away.
                </p>
              )}
              {!isEditMode && linkedEntitlement && (
                <p
                  data-testid="badge-naming-hint"
                  className="mt-1 text-xs text-sky-700 dark:text-sky-300"
                >
                  By convention a badge linked to{' '}
                  <code>{linkedEntitlement}</code> has the ID{' '}
                  <code>{suggestedBadgeId}</code>
                  {suggestedName && (
                    <> and the name &ldquo;{suggestedName}&rdquo;</>
                  )}
                  . A suggestion only; any ID and name are accepted.
                </p>
              )}
              {isEditMode && originalLink && !linkedEntitlement && (
                <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                  Saving unlinks the badge. Its automatic grants are removed at
                  the next hourly reconcile; grants made by hand stay.
                  {originalAutomaticOnly &&
                    ' It is no longer automatic-only either.'}
                </p>
              )}

              {/* Automatic only (#2268) */}
              <label className="mt-3 flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  data-testid="badge-automatic-only"
                  className="mt-1"
                  checked={automaticOnlyEffective}
                  disabled={isLoading || !linkedEntitlement}
                  onChange={e => setAutomaticOnly(e.target.checked)}
                />
                <span>
                  Automatic only
                  <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                    {linkedEntitlement ? (
                      <>
                        The badge exists on a user only while they hold{' '}
                        <code>{linkedEntitlement}</code>. It cannot be granted
                        by hand or by a redeem code, and grants made by hand are
                        turned into automatic ones, or removed if the user does
                        not hold the entitlement, at the next hourly reconcile.
                      </>
                    ) : (
                      'Needs a linked entitlement above: an automatic-only badge is granted only by one.'
                    )}
                  </span>
                </span>
              </label>
            </div>

            {/* Badge Icon Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Badge Icon {!isEditMode && !imageless && '*'}
              </label>

              {!isEditMode && (
                <label className="mb-3 flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={imageless}
                    disabled={isLoading}
                    onChange={e => {
                      const next = e.target.checked;
                      setImageless(next);
                      // Drop any file already chosen. Otherwise ticking the box
                      // and then unticking it leaves a selection the admin can
                      // no longer see but which would still be uploaded.
                      if (next) {
                        setSelectedFile(null);
                      }
                      setUploadError(null);
                    }}
                  />
                  <span>
                    This badge has no icon
                    <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                      For a badge that drives styling rather than showing a
                      picture. <strong>This cannot be changed later</strong> —
                      correcting it means deleting the badge, which also removes
                      it from everyone who has earned it.
                    </span>
                  </span>
                </label>
              )}

              {isImageless ? (
                <p className="rounded-md border border-dashed border-gray-300 p-3 text-sm text-gray-600 dark:border-gray-600 dark:text-gray-400">
                  This badge has no icon by design, so there is nothing to
                  upload. The API refuses an icon for it.
                </p>
              ) : (
                <>
                  <BadgeIconUploader
                    onFileSelect={file => {
                      setSelectedFile(file);
                      setUploadError(null);
                    }}
                    currentIconUrl={badge?.icon_url || null}
                    disabled={isLoading}
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {isEditMode
                      ? 'Upload a new icon to replace the current one (optional)'
                      : 'Upload a custom SVG icon (required)'}
                  </p>
                </>
              )}
            </div>

            {/* Upload/Form Error Messages */}
            {uploadError && (
              <AnimatedMessage type="error" message={uploadError} />
            )}
            {(createBadge.error || updateBadge.error) && (
              <AnimatedMessage
                type="error"
                message={
                  createBadge.error?.message ||
                  updateBadge.error?.message ||
                  'An error occurred'
                }
              />
            )}

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-4">
              <FormButton
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
                className="flex-1"
              >
                Cancel
              </FormButton>
              <FormButton
                type="submit"
                variant="primary"
                disabled={
                  !isValid ||
                  isLoading ||
                  checkingBadgeId ||
                  !!badgeIdError ||
                  // The second enforcement point for "an icon is required".
                  // The handler above is the first; changing only one leaves
                  // the button greyed out with no explanation.
                  (!isEditMode && !imageless && !selectedFile)
                }
                isLoading={isLoading}
                loadingText={
                  isUploading
                    ? 'Uploading icon...'
                    : isEditMode
                      ? 'Updating...'
                      : 'Creating...'
                }
                className="flex-1"
              >
                {isEditMode ? 'Update Badge' : 'Create Badge'}
              </FormButton>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
